import type { Dispatch, SetStateAction } from 'react';
import type React from 'react';

/** The 6 web onboarding steps (+ failed terminal state). */
export type WebStepKey =
  | 'on' // 1. Turn on device
  | 'wificonf' // 2. Enter Wi-Fi & server details
  | 'deviceWifi' // 3. Connect laptop to device hotspot
  | 'sendWifi' // 4. Send Wi-Fi details to device
  | 'internet' // 5. Connect to the internet
  | 'done' // 6. Device connected
  | 'failed'; // 6. Device connection failed

/** The 4 sidebar phases shown in the stepper. */
export type WebPhase = 'hardware' | 'registration' | 'wifi' | 'completion';

export const WEB_STEPS: WebStepKey[] = [
  'on',
  'wificonf',
  'deviceWifi',
  'sendWifi',
  'internet',
  'done',
  'failed',
];

/** Human step number (1–6) for the "Step X of 6" titles. */
export const WEB_STEP_NUMBER: Record<WebStepKey, number> = {
  on: 1,
  wificonf: 2,
  deviceWifi: 3,
  sendWifi: 4,
  internet: 5,
  done: 6,
  failed: 6,
};

/** Maps each step to its sidebar phase. */
export const WEB_STEP_PHASE: Record<WebStepKey, WebPhase> = {
  on: 'hardware',
  wificonf: 'registration',
  deviceWifi: 'wifi',
  sendWifi: 'wifi',
  internet: 'wifi',
  done: 'completion',
  failed: 'completion',
};

export const WEB_PHASES: { key: WebPhase; label: string }[] = [
  { key: 'hardware', label: 'Hardware Setup' },
  { key: 'registration', label: 'Registration' },
  { key: 'wifi', label: 'Wi-Fi Config' },
  { key: 'completion', label: 'Completion' },
];

export interface WifiValidationErrors {
  wifiname?: string;
  password?: string;
}

export interface WebFlowCtx {
  // Navigation
  step: WebStepKey;
  goTo: (_s: WebStepKey) => void;
  goBack: () => void;

  // Caller-provided
  serialNumber: string;
  deviceId?: string;

  // Form state
  wifiname: string;
  setWifiName: Dispatch<SetStateAction<string>>;
  wifipassword: string;
  setWifiPassword: Dispatch<SetStateAction<string>>;
  serverUrl: string;
  setServerUrl: Dispatch<SetStateAction<string>>;

  validationErrors: WifiValidationErrors;

  // Status
  loading: boolean;
  /** True when the device is being onboarded for the first time (REGISTERED + UNKNOWN). */
  isFirstOnboarding: boolean;
  /** HTTPS page → Step 4 opens the device captive portal instead of POSTing. */
  isSecurePage: boolean;
  /** Checklist errors shown on Step 3 when device ping fails. */
  deviceWifiError: React.ReactNode[];
  /** Inline error shown on Step 5 when internet check fails. */
  internetError: string;

  // Actions
  verify: () => void; // Step 2 Next → confirm modal
  confirmWifiDetails: () => void; // Confirm modal → deviceWifi step
  connectToDeviceWifi: () => Promise<void>; // Step 3 "I'm Connected" → ping verify → Step 4
  sendWifiDetails: () => Promise<void>; // Step 4 Connect → POST to device
  connectInternet: () => Promise<void>; // Step 5 → verify online + update backend
  navigateToDashboard: () => void;
  navigateToAddDevice: () => void;

  // Exit confirmation
  openExitModal: () => void;
}
